import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Badge,
  Drawer,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { getFirestore, collection, query, where, getDocs, orderBy as fbOrderBy, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import firebaseApp from '../../utils/firebaseConfig';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const STRIPE_PK = 'pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#F8F9FA',
});

const Header = styled(Box)({
  backgroundColor: '#333',
  color: '#FFF',
  padding: '20px 0',
  marginBottom: 32,
});

const PlanCard = styled(Card)(({ selected }) => ({
  cursor: 'pointer',
  border: selected ? '2px solid #1976d2' : '2px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
}));

const normalizePhone = (raw) => raw.replace(/\D/g, '');
const isPhoneInput = (val) => {
  const digits = val.replace(/\D/g, '');
  return digits.length >= 7 && /^\d+$/.test(digits);
};
const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// La evidencia se guarda como imagen comprimida (JPEG base64) directo en
// Firestore — sin depender de Storage ni de funciones nuevas. Se comprime a
// máx 1200px para que quepa holgada bajo el límite de 1MB por documento.
const compressImageToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('El archivo no es una imagen válida'));
    img.onload = () => {
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const scale = MAX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      let dataUrl = canvas.toDataURL('image/jpeg', 0.75);
      // Si aún queda muy pesada (fotos con mucho detalle), bajar calidad
      if (dataUrl.length > 850 * 1024) dataUrl = canvas.toDataURL('image/jpeg', 0.5);
      if (dataUrl.length > 950 * 1024) {
        reject(new Error('La imagen es demasiado grande. Intenta con otra foto.'));
        return;
      }
      resolve(dataUrl);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});
const intervalLabel = (months) => {
  const m = months || 12;
  if (m === 12) return '1 año';
  if (m % 12 === 0) return `${m / 12} años`;
  return `${m} meses`;
};

// ─── Precio de renovación para miembros activos ───
// Un miembro califica mientras su membresía esté vigente o hasta los días de
// gracia del plan (default 15) después de vencida. La decisión final del
// precio la toma el backend (createMembershipSubscription); esto es solo para
// mostrar el precio correcto en la tienda.
const RENEWAL_DEFAULT_GRACE_DAYS = 15;

const toExpirationDate = (exp) => {
  if (!exp) return null;
  if (typeof exp.toDate === 'function') return exp.toDate();
  // localStorage serializa el Timestamp como { seconds, nanoseconds }
  if (typeof exp.seconds === 'number') return new Date(exp.seconds * 1000);
  const d = new Date(exp);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isRenewalEligibleFor = (member, plan) => {
  if (!member) return false;
  const graceDays = Number(plan?.renewalGraceDays) > 0 ? Number(plan.renewalGraceDays) : RENEWAL_DEFAULT_GRACE_DAYS;
  const expDate = toExpirationDate(member.expiration);
  if (expDate) return Date.now() <= expDate.getTime() + graceDays * 24 * 60 * 60 * 1000;
  return member.active === true;
};

export default function StorePage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // User identification
  const [loginInput, setLoginInput] = useState('');
  const [userData, setUserData] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({ firstName: '', lastName: '', phone: '', email: '', licenseNumber: '' });

  // Payment
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [addingCard, setAddingCard] = useState(false);
  const [cardElementReady, setCardElementReady] = useState(false);
  const [addCardError, setAddCardError] = useState('');
  const [stripeInstance, setStripeInstance] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState('');

  // Checkout
  const [checkoutStep, setCheckoutStep] = useState('browse'); // browse, identify, payment, processing, success
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  // Evidencia para tarifas especiales (menores, policías/agentes)
  const [evidenceFiles, setEvidenceFiles] = useState([]); // [{ name, dataUrl }]
  const [evidenceError, setEvidenceError] = useState('');

  useEffect(() => {
    fetchPlans();
    const saved = localStorage.getItem('store_user');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.email) setUserEmail(parsed.email);
        setUserData(parsed.id ? parsed : null);
        setIsLoggedIn(true);
        if (parsed.email) loadPaymentMethods(parsed.email);
        // Refrescar los datos del miembro (expiration/active pueden haber
        // cambiado desde que se guardó la sesión) para que el precio de
        // renovación se calcule con datos al día
        if (parsed.id) {
          getDoc(doc(db, 'members', parsed.id)).then((snap) => {
            if (snap.exists()) setUserData({ id: snap.id, ...snap.data() });
          }).catch(() => { /* ignore */ });
        }
      } catch (e) { /* ignore */ }
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const q = query(
        collection(db, 'membership_plans'),
        where('active', '==', true),
        fbOrderBy('sortOrder', 'asc')
      );
      const snap = await getDocs(q);
      setPlans(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
    setLoading(false);
  };

  const loadPaymentMethods = async (email) => {
    try {
      const hapQ = query(collection(db, 'hap-members'), where('email', '==', email));
      const hapSnap = await getDocs(hapQ);
      if (!hapSnap.empty) {
        const hap = hapSnap.docs[0].data();
        if (hap.stripeCustomerId) {
          setStripeCustomerId(hap.stripeCustomerId);
          const res = await axios.post(`${FUNCTIONS_BASE}/listPaymentMethods`, { customerId: hap.stripeCustomerId });
          if (res.data.paymentMethods) {
            setPaymentMethods(res.data.paymentMethods);
            if (res.data.paymentMethods.length > 0) {
              setSelectedPM(res.data.paymentMethods[0].id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading payment methods:', err);
    }
  };

  const handleLogin = async () => {
    const raw = loginInput.trim();
    if (!raw) { setLoginError('Ingresa tu email o número de teléfono'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      let snap;
      let lookupEmail = '';
      let lookupPhone = '';

      if (isPhoneInput(raw)) {
        lookupPhone = normalizePhone(raw);
        const q = query(collection(db, 'members'), where('phone', '==', lookupPhone));
        snap = await getDocs(q);
      } else {
        lookupEmail = raw.toLowerCase();
        const q = query(collection(db, 'members'), where('email', '==', lookupEmail));
        snap = await getDocs(q);
      }

      if (snap.empty) {
        setShowRegister(true);
        if (lookupPhone) {
          setRegForm((prev) => ({ ...prev, phone: lookupPhone }));
        } else {
          setRegForm((prev) => ({ ...prev, email: lookupEmail }));
        }
        setLoginLoading(false);
        return;
      }

      const memberDoc = snap.docs[0];
      const member = { id: memberDoc.id, ...memberDoc.data() };
      setUserData(member);
      setIsLoggedIn(true);
      if (member.email) setUserEmail(member.email);
      localStorage.setItem('store_user', JSON.stringify({ ...member, email: member.email || lookupEmail }));
      if (member.email) await loadPaymentMethods(member.email);
      setCheckoutStep('payment');
    } catch (err) {
      setLoginError('Error al verificar tu cuenta');
    }
    setLoginLoading(false);
  };

  const handleRegister = async () => {
    const { firstName, lastName, phone, email, licenseNumber } = regForm;
    if (!firstName.trim() || !lastName.trim()) { setLoginError('Nombre y apellido son requeridos'); return; }
    if (!phone.trim() && !email.trim()) { setLoginError('Teléfono o email es requerido'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const normalizedPhone = phone ? normalizePhone(phone) : '';
      const normalizedEmail = email ? email.toLowerCase().trim() : '';

      const newRef = doc(collection(db, 'members'));
      const memberData = {
        fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: normalizedPhone,
        email: normalizedEmail,
        licenseNumber: licenseNumber.trim(),
        active: false,
        createdAt: serverTimestamp(),
      };
      await setDoc(newRef, memberData);

      const member = { id: newRef.id, ...memberData };
      setUserData(member);
      setIsLoggedIn(true);
      if (normalizedEmail) setUserEmail(normalizedEmail);
      localStorage.setItem('store_user', JSON.stringify({ ...member, email: normalizedEmail }));
      setShowRegister(false);
      setCheckoutStep('payment');
    } catch (err) {
      console.error('Error creating member:', err);
      setLoginError('Error al crear tu cuenta. Intenta de nuevo.');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('store_user');
    setUserData(null);
    setUserEmail('');
    setIsLoggedIn(false);
    setStripeCustomerId(null);
    setPaymentMethods([]);
    setSelectedPM('');
    setShowRegister(false);
    setRegForm({ firstName: '', lastName: '', phone: '', email: '', licenseNumber: '' });
    setCheckoutStep('browse');
  };

  const addToCart = (plan) => {
    if (cart.some((c) => c.id === plan.id)) return;
    setCart([...cart, plan]);
  };

  const removeFromCart = (planId) => {
    setCart(cart.filter((c) => c.id !== planId));
  };

  // Si el cliente identificado califica como renovación, en la tienda se le
  // muestra (y cobra) el precio de renovación del plan
  const renewalApplies = (plan) =>
    isLoggedIn && userData && Number(plan.renewalPriceCents) > 0 &&
    isRenewalEligibleFor(userData, plan);

  const effectivePriceCents = (plan) => (renewalApplies(plan) ? plan.renewalPriceCents : (plan.priceCents || 0));

  // IVU 11.5% (10.5% estatal + 1% municipal) — mismo cálculo que el backend
  // (createMembershipPayment cobra subtotal + impuesto)
  const TAX_RATE = 0.115;
  const cartSubtotal = cart.reduce((sum, item) => sum + effectivePriceCents(item), 0);
  const cartTax = Math.round(cartSubtotal * TAX_RATE);
  const cartTotal = cartSubtotal + cartTax;

  const handleStartAddCard = async () => {
    setAddingCard(true);
    setAddCardError('');
    try {
      const email = userEmail || null;
      const res = await axios.post(`${FUNCTIONS_BASE}/createSetupIntent`, {
        email,
        customerId: stripeCustomerId || undefined,
      });
      const { clientSecret, customer } = res.data;
      setSetupClientSecret(clientSecret);
      if (customer) setStripeCustomerId(customer);

      const stripeObj = await loadStripe(STRIPE_PK);
      setStripeInstance(stripeObj);

      const elements = stripeObj.elements({ clientSecret });
      // Campos separados: el element 'card' de una sola línea no cabe en
      // pantallas angostas y solapaba "MM / AA" con el CVC.
      const elStyle = {
        base: { fontSize: '16px', color: '#333', '::placeholder': { color: '#999' } },
        invalid: { color: '#d40100' },
      };
      const number = elements.create('cardNumber', { style: elStyle, placeholder: '1234 1234 1234 1234' });
      const expiry = elements.create('cardExpiry', { style: elStyle });
      const cvc = elements.create('cardCvc', { style: elStyle });
      setCardElement({ number, expiry, cvc });
      setTimeout(() => {
        const container = document.getElementById('store-stripe-card-number');
        if (container) {
          number.mount('#store-stripe-card-number');
          expiry.mount('#store-stripe-card-expiry');
          cvc.mount('#store-stripe-card-cvc');
          setCardElementReady(true);
        }
      }, 100);
    } catch (err) {
      console.error('Error starting add card:', err);
      setAddCardError('Error al iniciar. Intenta de nuevo.');
    }
  };

  const handleConfirmAddCard = async () => {
    if (!stripeInstance || !cardElement || !setupClientSecret) return;
    setAddCardError('');
    setProcessing(true);
    try {
      const { error: stripeError, setupIntent } = await stripeInstance.confirmCardSetup(setupClientSecret, {
        // Con campos separados basta el de número; Stripe toma fecha y CVC
        payment_method: { card: cardElement.number },
      });
      if (stripeError) {
        setAddCardError(stripeError.message);
        setProcessing(false);
        return;
      }
      if (setupIntent.status === 'succeeded') {
        Object.values(cardElement).forEach((el) => { try { el.unmount(); } catch (e) { /* ignore */ } });
        setAddingCard(false);
        setCardElementReady(false);
        setCardElement(null);
        setSetupClientSecret('');
        const custId = stripeCustomerId || setupIntent.customer;
        if (custId) {
          setStripeCustomerId(custId);
          const pmRes = await axios.post(`${FUNCTIONS_BASE}/listPaymentMethods`, { customerId: custId });
          if (pmRes.data.paymentMethods) {
            setPaymentMethods(pmRes.data.paymentMethods);
            if (pmRes.data.paymentMethods.length > 0) {
              setSelectedPM(pmRes.data.paymentMethods[pmRes.data.paymentMethods.length - 1].id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error confirming card:', err);
      setAddCardError('Error al guardar la tarjeta. Intenta de nuevo.');
    }
    setProcessing(false);
  };

  const handleCancelAddCard = () => {
    if (cardElement) {
      Object.values(cardElement).forEach((el) => { try { el.unmount(); } catch (e) { /* ignore */ } });
    }
    setAddingCard(false);
    setCardElementReady(false);
    setCardElement(null);
    setSetupClientSecret('');
    setAddCardError('');
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    if (isLoggedIn && userData) {
      setCheckoutStep('payment');
    } else {
      setCheckoutStep('identify');
    }
    setDrawerOpen(true);
  };

  // Términos de renovación: el cliente acepta que se le cobre el plan a ese
  // método de pago al vencer el término (anual u otro). Requerido para pagar
  // o enviar solicitud (pedido 2026-09-15).
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Ojo: no llamarla intervalLabel — taparía la de arriba (que recibe meses) y
  // un plan de 6 meses salía como "anualmente" en la tarjeta y el carrito.
  const renewalCadenceLabel = (plan) => {
    const m = Number(plan?.intervalMonths) || 12;
    if (m === 12) return 'anualmente';
    if (m === 1) return 'mensualmente';
    if (m === 6) return 'cada 6 meses';
    if (m === 3) return 'cada 3 meses';
    return `cada ${m} meses`;
  };

  const handlePurchase = async () => {
    if (!userData || !selectedPM || cart.length === 0) return;
    if (!acceptTerms) {
      setError('Debes aceptar los términos de renovación para continuar.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const plan = cart[0];

      if (!stripeCustomerId) {
        setError('No se encontró tu cuenta de pago. Agrega un método de pago primero.');
        setProcessing(false);
        return;
      }

      // Pago único (sin Stripe Subscriptions): el backend decide el monto
      // (regular o renovación) y la vigencia se maneja en Firestore
      const payRes = await axios.post(`${FUNCTIONS_BASE}/createMembershipPayment`, {
        stripeCustomerId,
        planId: plan.id,
        memberId: userData.id,
        memberEmail: userEmail || userData.email || '',
      });

      const { clientSecret, paymentIntentId } = payRes.data;

      if (clientSecret) {
        const stripeObj = stripeInstance || await loadStripe(STRIPE_PK);
        const { error: confirmError, paymentIntent } = await stripeObj.confirmCardPayment(clientSecret, {
          payment_method: selectedPM,
        });

        if (confirmError) {
          setError(confirmError.message);
          setProcessing(false);
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          const activateRes = await axios.post(`${FUNCTIONS_BASE}/activateMemberAfterPayment`, {
            memberId: userData.id,
            membershipPlanId: plan.id,
            paymentIntentId,
          });

          // La compra directa también queda registrada en membership_requests
          // (ya aprobada) para que el staff vea QUIÉN compró y pagó desde
          // Solicitudes de Membresía (pedido 2026-09-15).
          try {
            const pmDirect = paymentMethods.find((p) => p.id === selectedPM);
            await setDoc(doc(collection(db, 'membership_requests')), {
              memberId: userData.id,
              memberName: userData.fullName || '',
              memberPhone: userData.phone || '',
              memberEmail: userEmail || userData.email || '',
              planId: plan.id,
              planName: plan.name,
              priceCents: effectivePriceCents(plan),
              taxCents: Math.round(effectivePriceCents(plan) * TAX_RATE),
              totalCents: effectivePriceCents(plan) + Math.round(effectivePriceCents(plan) * TAX_RATE),
              isRenewal: renewalApplies(plan),
              intervalMonths: plan.intervalMonths || 12,
              stripeCustomerId,
              paymentMethodId: selectedPM,
              paymentMethodLabel: pmDirect?.card ? `${(pmDirect.card.brand || '').toUpperCase()} •••• ${pmDirect.card.last4}` : '',
              paymentIntentId: paymentIntentId || '',
              evidenceIds: [],
              status: 'approved',
              autoApproved: true,
              acceptedRenewalTerms: true,
              termsAcceptedAt: serverTimestamp(),
              expiration: activateRes.data?.expiration || null,
              decidedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            });
          } catch (logErr) {
            console.error('No se pudo registrar la compra en membership_requests:', logErr);
          }

          setSuccessData({
            planName: plan.name,
            expiration: activateRes.data.expiration,
            memberName: userData.fullName,
          });
          setCheckoutStep('success');
          setCart([]);
        }
      }
    } catch (err) {
      console.error('Error processing purchase:', err);
      setError(err.response?.data?.error || 'Error procesando el pago. Intenta de nuevo.');
    }
    setProcessing(false);
  };

  // ─── Tarifas especiales: solicitud con evidencia ───
  // NO se cobra aquí. Se guarda la evidencia + la tarjeta escogida y la
  // solicitud queda pendiente; al aprobarla en el dashboard se hace el cobro.

  const handleEvidenceSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // permitir re-seleccionar el mismo archivo
    setEvidenceError('');
    try {
      // Máximo 2 imágenes para mantener el documento liviano
      const toAdd = files.slice(0, 2 - evidenceFiles.length);
      const compressed = await Promise.all(toAdd.map(async (f) => ({
        name: f.name,
        dataUrl: await compressImageToDataUrl(f),
      })));
      setEvidenceFiles([...evidenceFiles, ...compressed].slice(0, 2));
    } catch (err) {
      setEvidenceError(err.message || 'Error procesando la imagen');
    }
  };

  const handleSubmitRequest = async () => {
    if (!userData || !selectedPM || cart.length === 0 || evidenceFiles.length === 0) return;
    if (!acceptTerms) {
      setError('Debes aceptar los términos de renovación para continuar.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const plan = cart[0];

      if (!stripeCustomerId) {
        setError('No se encontró tu cuenta de pago. Agrega un método de pago primero.');
        setProcessing(false);
        return;
      }

      // Evitar solicitudes duplicadas del mismo plan
      const dupQ = query(
        collection(db, 'membership_requests'),
        where('memberId', '==', userData.id),
        where('planId', '==', plan.id),
        where('status', '==', 'pending')
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        setError('Ya tienes una solicitud pendiente para esta membresía. Te avisaremos cuando sea revisada.');
        setProcessing(false);
        return;
      }

      // La evidencia va en documentos aparte para que la lista de solicitudes
      // del dashboard no cargue las imágenes hasta que se abran
      const evidenceIds = await Promise.all(evidenceFiles.map(async (ev) => {
        const evRef = doc(collection(db, 'membership_request_evidence'));
        await setDoc(evRef, { name: ev.name, dataUrl: ev.dataUrl, createdAt: serverTimestamp() });
        return evRef.id;
      }));

      const pm = paymentMethods.find((p) => p.id === selectedPM);
      const reqRef = doc(collection(db, 'membership_requests'));
      await setDoc(reqRef, {
        memberId: userData.id,
        memberName: userData.fullName || '',
        memberPhone: userData.phone || '',
        memberEmail: userEmail || userData.email || '',
        planId: plan.id,
        planName: plan.name,
        // Si aplica renovación, la solicitud guarda el precio y el price de
        // Stripe de renovación — al aprobar se cobra ese monto
        priceCents: effectivePriceCents(plan),
        // Desglose con IVU 11.5%: al aprobar se cobra totalCents
        taxCents: Math.round(effectivePriceCents(plan) * TAX_RATE),
        totalCents: effectivePriceCents(plan) + Math.round(effectivePriceCents(plan) * TAX_RATE),
        isRenewal: renewalApplies(plan),
        intervalMonths: plan.intervalMonths || 12,
        stripePriceId: (renewalApplies(plan) ? plan.stripeRenewalPriceId : plan.stripePriceId) || '',
        stripeCustomerId,
        paymentMethodId: selectedPM,
        paymentMethodLabel: pm?.card ? `${(pm.card.brand || '').toUpperCase()} •••• ${pm.card.last4}` : '',
        evidenceIds,
        status: 'pending',
        acceptedRenewalTerms: true,
        termsAcceptedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

      setSuccessData({
        planName: plan.name,
        memberName: userData.fullName,
        pendingApproval: true,
      });
      setCheckoutStep('success');
      setCart([]);
      setEvidenceFiles([]);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Error enviando la solicitud. Intenta de nuevo.');
    }
    setProcessing(false);
  };

  // ─── Render ────────────────────────────────────

  const renderPlans = () => (
    <Grid container spacing={3}>
      {plans.map((plan) => {
        const inCart = cart.some((c) => c.id === plan.id);
        return (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <PlanCard selected={inCart} elevation={inCart ? 4 : 1}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <CardMembershipIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                  {inCart && <Chip label="En carrito" size="small" color="primary" />}
                </Stack>
                <Typography variant="h5" fontWeight="bold" gutterBottom>{plan.name}</Typography>
                {plan.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {plan.description}
                  </Typography>
                )}
                {renewalApplies(plan) ? (
                  <>
                    <Typography variant="h4" color="success.main" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatPrice(plan.renewalPriceCents)}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{intervalLabel(plan.intervalMonths)}
                      </Typography>
                      <Typography component="span" variant="body1" color="text.disabled" sx={{ ml: 1, textDecoration: 'line-through' }}>
                        {formatPrice(plan.priceCents)}
                      </Typography>
                    </Typography>
                    <Chip label="Precio de renovación para miembros" size="small" color="success" sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      + IVU 11.5% (10.5% estatal + 1% municipal) = <b>{formatPrice(plan.renewalPriceCents + Math.round(plan.renewalPriceCents * TAX_RATE))}</b>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h4" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {formatPrice(plan.priceCents)}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{intervalLabel(plan.intervalMonths)}
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      + IVU 11.5% (10.5% estatal + 1% municipal) = <b>{formatPrice((plan.priceCents || 0) + Math.round((plan.priceCents || 0) * TAX_RATE))}</b>
                    </Typography>
                    {Number(plan.renewalPriceCents) > 0 && (
                      <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                        Miembros activos renuevan por <strong>{formatPrice(plan.renewalPriceCents)}</strong>
                      </Typography>
                    )}
                  </>
                )}
                <Chip label={`Renovación cada ${intervalLabel(plan.intervalMonths)}`} size="small" color="info" variant="outlined" sx={{ mb: 2 }} />
                {plan.requiresEvidence && (
                  <Chip
                    label="Requiere evidencia y aprobación"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mb: 2, ml: 0.5 }}
                  />
                )}
                {plan.benefits && (
                  <Stack spacing={0.5} sx={{ mt: 2, mb: 2 }}>
                    {plan.benefits.split('\n').filter(Boolean).map((b, i) => (
                      <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#28a745' }} /> {b.trim()}
                      </Typography>
                    ))}
                  </Stack>
                )}
                <Button
                  fullWidth
                  variant={inCart ? 'outlined' : 'contained'}
                  color={inCart ? 'error' : 'primary'}
                  size="large"
                  onClick={() => inCart ? removeFromCart(plan.id) : addToCart(plan)}
                  startIcon={inCart ? <DeleteIcon /> : <ShoppingCartIcon />}
                  sx={{ mt: 1 }}
                >
                  {inCart ? 'Remover' : 'Agregar al carrito'}
                </Button>
              </CardContent>
            </PlanCard>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderIdentifyStep = () => (
    <Box>
      {!showRegister ? (
        <>
          <Typography variant="h6" gutterBottom>Identificación</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Ingresa tu teléfono o email para encontrar tu cuenta.
          </Typography>
          <TextField
            fullWidth
            label="Teléfono o Email"
            value={loginInput}
            onChange={(e) => setLoginInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="787-438-0914 o tu@email.com"
            sx={{ mb: 2 }}
          />
          {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={loginLoading}
          >
            {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Buscar mi cuenta'}
          </Button>
        </>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>Registro de Nuevo Miembro</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            No encontramos una cuenta existente. Completa tus datos para continuar.
          </Alert>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Nombre"
              value={regForm.firstName}
              onChange={(e) => setRegForm({ ...regForm, firstName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Apellido"
              value={regForm.lastName}
              onChange={(e) => setRegForm({ ...regForm, lastName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Teléfono"
              value={regForm.phone}
              onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
              placeholder="787-438-0914"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={regForm.email}
              onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
              placeholder="tu@email.com"
            />
            <TextField
              fullWidth
              label="Número de Licencia de Tiro"
              value={regForm.licenseNumber}
              onChange={(e) => setRegForm({ ...regForm, licenseNumber: e.target.value })}
              placeholder="Opcional"
            />
          </Stack>
          {loginError && <Alert severity="error" sx={{ mt: 2 }}>{loginError}</Alert>}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleRegister}
            disabled={loginLoading}
            sx={{ mt: 3 }}
          >
            {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Registrarme y Continuar'}
          </Button>
          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={() => { setShowRegister(false); setLoginError(''); }}
            sx={{ mt: 1 }}
          >
            Volver a buscar cuenta
          </Button>
        </>
      )}
    </Box>
  );

  const renderPaymentStep = () => {
    const needsEvidence = cart.some((c) => c.requiresEvidence);
    const evidencePlan = cart.find((c) => c.requiresEvidence);
    return (
    <Box>
      {userData && (
        <Card variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f0f7ff' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Cuenta</Typography>
              <Typography variant="subtitle1" fontWeight="bold">{userData.fullName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {userData.phone} {userData.email ? `· ${userData.email}` : ''}
              </Typography>
            </Box>
            <Button size="small" color="error" onClick={handleLogout}>Cerrar sesión</Button>
          </Stack>
        </Card>
      )}

      <Typography variant="h6" gutterBottom>Resumen</Typography>
      {cart.map((item) => (
        <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
          <Box>
            <Typography variant="subtitle2">{item.name}</Typography>
            <Typography variant="caption" color="text.secondary">Membresía · {intervalLabel(item.intervalMonths)}</Typography>
            {renewalApplies(item) && (
              <Chip label="Precio de renovación de miembro" size="small" color="success" sx={{ display: 'flex', width: 'fit-content', mt: 0.5 }} />
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {renewalApplies(item) && (
              <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through', display: 'block' }}>
                {formatPrice(item.priceCents)}
              </Typography>
            )}
            <Typography variant="subtitle1" fontWeight="bold">{formatPrice(effectivePriceCents(item))}</Typography>
          </Box>
        </Stack>
      ))}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
        <Typography variant="body2">{formatPrice(cartSubtotal)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">IVU 11.5% (10.5% estatal + 1% municipal)</Typography>
        <Typography variant="body2">{formatPrice(cartTax)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h6">Total</Typography>
        <Typography variant="h6" fontWeight="bold" color="primary">{formatPrice(cartTotal)}</Typography>
      </Stack>

      <Typography variant="subtitle2" gutterBottom>Método de Pago</Typography>
      {paymentMethods.length > 0 && (
        <RadioGroup value={selectedPM} onChange={(e) => setSelectedPM(e.target.value)}>
          {paymentMethods.map((pm) => (
            <FormControlLabel
              key={pm.id}
              value={pm.id}
              control={<Radio />}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PaymentIcon fontSize="small" />
                  <Typography variant="body2">
                    {pm.card?.brand?.toUpperCase()} •••• {pm.card?.last4} — {pm.card?.exp_month}/{pm.card?.exp_year}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </RadioGroup>
      )}

      {addingCard ? (
        <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Nueva tarjeta</Typography>
          <Box id="store-stripe-card-number" sx={{ p: 1.75, border: '1px solid #ddd', borderRadius: 1, mb: 1, minHeight: 44 }} />
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Box id="store-stripe-card-expiry" sx={{ flex: 1, minWidth: 0, p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44 }} />
            <Box id="store-stripe-card-cvc" sx={{ flex: 1, minWidth: 0, p: 1.75, border: '1px solid #ddd', borderRadius: 1, minHeight: 44 }} />
          </Stack>
          {addCardError && <Alert severity="error" sx={{ mb: 1 }}>{addCardError}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={handleConfirmAddCard} disabled={!cardElementReady || processing}>
              {processing ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
            </Button>
            <Button size="small" onClick={handleCancelAddCard}>Cancelar</Button>
          </Stack>
        </Card>
      ) : (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleStartAddCard}
          sx={{ mt: 1, mb: 3 }}
          fullWidth
        >
          Agregar tarjeta
        </Button>
      )}

      {needsEvidence && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Evidencia requerida</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {evidencePlan?.evidenceLabel
              ? `Para esta membresía necesitamos: ${evidencePlan.evidenceLabel}`
              : 'Esta membresía tiene una tarifa especial y requiere evidencia (ej. ID, placa o credencial).'}
            {' '}Tu solicitud será revisada por nuestro equipo. <strong>No se cobrará nada hasta que sea aprobada.</strong>
          </Alert>

          {evidenceFiles.map((ev, i) => (
            <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1, borderBottom: '1px solid #eee' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Box component="img" src={ev.dataUrl} alt={ev.name} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }} />
                <Typography variant="body2" noWrap>{ev.name}</Typography>
              </Stack>
              <IconButton size="small" color="error" onClick={() => setEvidenceFiles(evidenceFiles.filter((_, j) => j !== i))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}

          {evidenceFiles.length < 2 && (
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} fullWidth sx={{ mt: 1 }}>
              {evidenceFiles.length === 0 ? 'Subir foto de evidencia' : 'Agregar otra foto (opcional)'}
              <input type="file" accept="image/*" hidden multiple onChange={handleEvidenceSelect} />
            </Button>
          )}
          {evidenceError && <Alert severity="error" sx={{ mt: 1 }}>{evidenceError}</Alert>}
        </Box>
      )}

      {/* Términos de renovación automática */}
      <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: acceptTerms ? '#C8E6C9' : '#E0E0E0', borderRadius: 2, bgcolor: acceptTerms ? '#F1F8E9' : '#FAFAFA' }}>
        <FormControlLabel
          control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} color="success" />}
          label={
            <Typography variant="body2">
              Acepto que mi membresía <strong>{cart[0]?.name}</strong> se cobre automáticamente{' '}
              <strong>{renewalCadenceLabel(cart[0])}</strong> al método de pago seleccionado, al vencer cada término,
              hasta que la cancele. Puedo cancelarla en cualquier momento desde la app o en el local.
            </Typography>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}

      {needsEvidence ? (
        <Button
          fullWidth
          variant="contained"
          size="large"
          color="warning"
          disabled={!selectedPM || processing || cart.length === 0 || evidenceFiles.length === 0 || !acceptTerms}
          onClick={handleSubmitRequest}
          sx={{ mt: 2, py: 1.5 }}
        >
          {processing ? <CircularProgress size={24} color="inherit" /> : 'Enviar solicitud para aprobación'}
        </Button>
      ) : (
        <Button
          fullWidth
          variant="contained"
          size="large"
          color="success"
          disabled={!selectedPM || processing || cart.length === 0 || !acceptTerms}
          onClick={handlePurchase}
          sx={{ mt: 2, py: 1.5 }}
        >
          {processing ? <CircularProgress size={24} color="inherit" /> : `Pagar ${formatPrice(cartTotal)}`}
        </Button>
      )}
      {needsEvidence && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
          Tu tarjeta se guarda de forma segura y solo se cobrará {formatPrice(cartTotal)} cuando tu solicitud sea aprobada.
        </Typography>
      )}
    </Box>
    );
  };

  const renderSuccess = () => (successData?.pendingApproval ? (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <HourglassTopIcon sx={{ fontSize: 80, color: '#FFAB00', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        ¡Solicitud Enviada!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {successData?.memberName}, recibimos tu solicitud para la membresía <strong>{successData?.planName}</strong>.
        Nuestro equipo revisará tu evidencia y te contactaremos. Tu tarjeta <strong>no será cobrada</strong> hasta
        que la solicitud sea aprobada.
      </Typography>
      <Box>
        <Button variant="contained" onClick={() => { setCheckoutStep('browse'); setDrawerOpen(false); setSuccessData(null); }}>
          Volver a la tienda
        </Button>
      </Box>
    </Box>
  ) : (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: '#28a745', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        ¡Membresía Activada!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {successData?.memberName}, tu membresía <strong>{successData?.planName}</strong> ha sido activada exitosamente.
      </Typography>
      {successData?.expiration && (
        <Chip
          label={`Válida hasta: ${new Date(successData.expiration).toLocaleDateString('es-PR', { year: 'numeric', month: 'long', day: 'numeric' })}`}
          color="success"
          sx={{ mb: 3 }}
        />
      )}
      <Box>
        <Button variant="contained" onClick={() => { setCheckoutStep('browse'); setDrawerOpen(false); setSuccessData(null); }}>
          Volver a la tienda
        </Button>
      </Box>
    </Box>
  ));

  return (
    <PageWrapper>
      <Header>
        <Container>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight="bold">B & B Target Center</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Tienda de Membresías</Typography>
            </Box>
            <IconButton color="inherit" onClick={() => setDrawerOpen(true)}>
              <Badge badgeContent={cart.length} color="error">
                <ShoppingCartIcon fontSize="large" />
              </Badge>
            </IconButton>
          </Stack>
        </Container>
      </Header>

      <Container sx={{ pb: 6 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>Membresías Disponibles</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Selecciona un plan de membresía para disfrutar de beneficios exclusivos.
        </Typography>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : plans.length === 0 ? (
          <Alert severity="info">No hay membresías disponibles en este momento.</Alert>
        ) : (
          renderPlans()
        )}
      </Container>

      {cart.length > 0 && !drawerOpen && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#333',
            color: '#FFF',
            py: 2,
            px: 3,
            zIndex: 1200,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2">{cart.length} {cart.length === 1 ? 'membresía' : 'membresías'} en el carrito</Typography>
                <Typography variant="h6" fontWeight="bold">{formatPrice(cartTotal)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatPrice(cartSubtotal)} + IVU 11.5% {formatPrice(cartTax)} (10.5% estatal + 1% municipal)
                </Typography>
              </Box>
              <Button variant="contained" color="success" size="large" onClick={handleCheckout}>
                Proceder al pago
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => { if (checkoutStep !== 'processing') setDrawerOpen(false); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 3 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6">
            {checkoutStep === 'success' ? 'Completado' : 'Carrito'}
          </Typography>
          {checkoutStep !== 'processing' && (
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>

        {checkoutStep === 'browse' && (
          <>
            {cart.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Tu carrito está vacío
              </Typography>
            ) : (
              <>
                {cart.map((item) => (
                  <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Box>
                      <Typography variant="subtitle2">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Anual</Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" fontWeight="bold">{formatPrice(effectivePriceCents(item))}</Typography>
                      <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body2">{formatPrice(cartSubtotal)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">IVU 11.5% (10.5% estatal + 1% municipal)</Typography>
                  <Typography variant="body2">{formatPrice(cartTax)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatPrice(cartTotal)}</Typography>
                </Stack>
                <Button fullWidth variant="contained" size="large" onClick={handleCheckout}>
                  Continuar
                </Button>
              </>
            )}
          </>
        )}

        {checkoutStep === 'identify' && renderIdentifyStep()}
        {checkoutStep === 'payment' && renderPaymentStep()}
        {checkoutStep === 'success' && renderSuccess()}
      </Drawer>
    </PageWrapper>
  );
}
